/**
 * Traducción de la interfaz.
 *
 * Spanish is the source: `ES` defines every key that exists, and `EN` is typed as
 * `Record<Clave, string>`, so a string added in one language and forgotten in the other fails
 * the build instead of shipping a Spanish word into an English screen.
 *
 * Only chrome is translated — labels, buttons, explanations. Everything the shop typed in stays
 * exactly as typed: product names, client names, what a gasto was for. Translating a user's own
 * words would be both wrong and unrecoverable.
 */

import { useSyncExternalStore } from 'react';
import { EN } from './en.js';
import { ES } from './es.js';
import { idiomaActual, suscribirIdioma, type Idioma } from './idioma.js';

export type Clave = keyof typeof ES;

const CATALOGOS: Record<Idioma, Record<Clave, string>> = { es: ES, en: EN };

export type Valores = Record<string, string | number>;

/** Replaces `{nombre}` with the value given. Absent placeholders are left alone, not blanked. */
function interpolar(texto: string, valores?: Valores): string {
  if (!valores) return texto;
  return texto.replace(/\{(\w+)\}/g, (completo, clave: string) =>
    clave in valores ? String(valores[clave]) : completo,
  );
}

export function traducir(clave: Clave, valores?: Valores): string {
  return interpolar(CATALOGOS[idiomaActual()][clave] ?? ES[clave] ?? clave, valores);
}

/**
 * Re-renders the component when the language changes.
 *
 * Returns the function rather than a string so a component calls `t('...')` as many times as it
 * likes from one subscription, and so the dependency on the current language is impossible to
 * forget — there is no way to read a translation without having subscribed.
 */
export function useT(): (clave: Clave, valores?: Valores) => string {
  useSyncExternalStore(suscribirIdioma, idiomaActual, () => 'es' as Idioma);
  return traducir;
}

export {
  cambiarIdioma,
  idiomaActual,
  localeActual,
  IDIOMAS,
  ETIQUETA_IDIOMA,
  ETIQUETA_IDIOMA_BREVE,
  type Idioma,
} from './idioma.js';
